// Lyra(궁수) 화살 투사체 렌더러(c022 D4a). 게임 이벤트 Ev.Arrow(발사 위치·방향·속도·사거리·종류·높이)를 받아 화살 몸통과 빛 꼬리를 그린다.
// 드로우콜 2: 화살 몸통 InstancedMesh 1 + 꼬리(가산) InstancedMesh 1. 동시 화살 상한 ARROW_CAP(가득 차면 가장 많이 날아간 것을 덮어쓴다).
// 생성 때 슬롯·지오메트리·머티리얼·행렬 임시값을 한 번만 만들고, fire·update는 형식 배열 값만 바꾼다(프레임 루프 할당 0).
// 좌표 규약은 게임과 같다: 방향 (dirX, dirZ)는 바닥 평면 단위 벡터(facing (sin, cos)), 높이 y는 발사 높이(m).
// 종류(Ev.Arrow kind): 0 기본 · 1 강사(3타 관통) · 2 관통 · 3 폭발 · 4 공중(아래로 내리꽂힘) · 5 유도 · 6 낙하(화살비, rain()으로만)
// 색 언어: 청록 바람 #3fd0c0 + 은 #c8d2dc(PRD 3절 Lyra 행). 섬광 없음(가산 꼬리 밝기만).
import * as THREE from 'three/webgpu';
import { abs, clamp, float, mix, smoothstep, uv, vec3 } from 'three/tsl';

/** 동시 화살 상한(모바일 예산). 각성 폭풍 화살비가 가장 많이 쓴다(0.5초 × 초당 약 120발 낙하) */
export const ARROW_CAP = 96;
export const ArrowKind = { Basic: 0, Heavy: 1, Pierce: 2, Explode: 3, Air: 4, Homing: 5, Rain: 6 } as const;
export const ARROW_KIND_COUNT = 7;

export const LYRA_TEAL = 0x3fd0c0;
export const LYRA_SILVER = 0xc8d2dc;

/** 종류별 모양 표: 몸통 배율, 꼬리 폭(m), 꼬리 길이(초 × 속도, 최대 m), 꼬리 밝기, 머리 색조(rgb 곱) */
export interface ArrowLook {
  readonly body: number;
  readonly trailW: number;
  readonly trailSec: number;
  readonly trailMax: number;
  readonly glow: number;
  readonly tint: readonly [number, number, number];
}
// 2026-11-03 vfx-look-art 감사: 내려보는 카메라(약 15m)에서 꼬리가 가는 청록 선으로만 보여 스킬이 읽히지 않았다(블라인드 P0).
// 꼬리 폭 약 2.2배·길이 약 2.5배로 바람 줄기처럼 읽히게 한다. 몸통은 그대로(실루엣은 화살).
export const ARROW_LOOK: readonly ArrowLook[] = [
  { body: 1.0, trailW: 0.3, trailSec: 0.12, trailMax: 3.6, glow: 1.8, tint: [1, 1, 1] },
  { body: 1.4, trailW: 0.72, trailSec: 0.2, trailMax: 7.0, glow: 2.6, tint: [1.1, 1.15, 1.15] },
  { body: 1.15, trailW: 0.5, trailSec: 0.18, trailMax: 6.0, glow: 2.2, tint: [0.95, 1.1, 1.1] },
  // 폭발: 머리에 따뜻한 심(은 + 연한 주황)이 섞여 '터질 화살'로 읽힌다
  { body: 1.2, trailW: 0.5, trailSec: 0.14, trailMax: 4.4, glow: 2.2, tint: [1.35, 1.0, 0.75] },
  { body: 1.0, trailW: 0.34, trailSec: 0.12, trailMax: 3.2, glow: 1.9, tint: [1, 1, 1] },
  { body: 0.95, trailW: 0.4, trailSec: 0.22, trailMax: 5.2, glow: 2.1, tint: [0.9, 1.1, 1.2] },
  { body: 1.05, trailW: 0.3, trailSec: 0.12, trailMax: 3.6, glow: 1.6, tint: [1, 1, 1] },
];

/** 공중 연사(4)의 기본 하강 각: 발사 높이에서 사거리 끝 바닥까지 곧게 내리꽂힌다 */
const AIR_DROP_TO = 0.05;
/** 낙하 화살 기본 속도(m/s) */
export const RAIN_SPEED = 26;

/** 화살 몸통 지오메트리(모델 공간: 머리 끝 = 원점, 꼬리 쪽 = -Z, 길이 0.9m). 정점 색 = 은 몸통 · 청록 머리·깃 */
export function buildArrowGeometry(): THREE.BufferGeometry {
  const silver = new THREE.Color(LYRA_SILVER);
  const teal = new THREE.Color(LYRA_TEAL).multiplyScalar(1.6);
  const parts: THREE.BufferGeometry[] = [];
  const paint = (g: THREE.BufferGeometry, c: THREE.Color): THREE.BufferGeometry => {
    const n = g.getAttribute('position').count;
    const col = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) { col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b; }
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    return g.index ? g.toNonIndexed() : g;
  };
  // 몸통: 가는 4각 기둥(-0.9 ~ -0.12)
  parts.push(paint(new THREE.CylinderGeometry(0.018, 0.018, 0.78, 4, 1).rotateX(Math.PI / 2).translate(0, 0, -0.51), silver));
  // 머리: 4각 뿔(-0.14 ~ 0)
  parts.push(paint(new THREE.ConeGeometry(0.05, 0.16, 4, 1).rotateX(Math.PI / 2).translate(0, 0, -0.08), teal));
  // 깃: 십자 판 두 장(-0.9 ~ -0.72)
  parts.push(paint(new THREE.BoxGeometry(0.12, 0.006, 0.18).translate(0, 0, -0.8), teal));
  parts.push(paint(new THREE.BoxGeometry(0.006, 0.12, 0.18).translate(0, 0, -0.8), teal));
  let total = 0;
  for (const p of parts) total += p.getAttribute('position').count;
  const pos = new Float32Array(total * 3), col = new Float32Array(total * 3);
  let o = 0;
  for (const p of parts) {
    const a = p.getAttribute('position').array as Float32Array, c = p.getAttribute('color').array as Float32Array;
    pos.set(a, o * 3); col.set(c, o * 3);
    o += p.getAttribute('position').count;
    p.dispose();
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  g.computeVertexNormals();
  return g;
}

/** 꼬리 지오메트리: 머리(z=0)에서 꼬리(z=-1)까지 십자 띠 두 장(폭 1). uv.x = 꼬리 0 → 머리 1 */
export function buildTrailGeometry(): THREE.BufferGeometry {
  const pos: number[] = [], uvs: number[] = [], idx: number[] = [];
  for (let s = 0; s < 2; s++) {
    const b = s * 4;
    // s 0 가로(바닥과 평행), 1 세로
    const hx = s === 0 ? 0.5 : 0, hy = s === 0 ? 0 : 0.5;
    pos.push(-hx, -hy, -1, hx, hy, -1, -hx, -hy, 0, hx, hy, 0);
    uvs.push(0, 0, 0, 1, 1, 0, 1, 1);
    idx.push(b, b + 2, b + 1, b + 1, b + 2, b + 3);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  g.setIndex(idx);
  return g;
}

function bodyMaterial(): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ vertexColors: true });
  m.fog = false;
  m.toneMapped = true;
  return m;
}

function trailMaterial(): THREE.MeshBasicNodeMaterial {
  // 인스턴스 색(청록 × 밝기, 끝날 때 어두워짐 = 가산이라 사라짐)이 이 색에 곱해진다
  const u = uv().x, v = uv().y;
  const band = abs(v.mul(2).sub(1));
  const across = band.oneMinus().pow(1.4);
  const along = u.pow(1.6);
  const core = band.oneMinus().pow(6).mul(smoothstep(0.55, 1.0, u));
  const col = mix(vec3(0.12, 0.78, 0.82), vec3(0.9, 0.98, 1.0), clamp(core.mul(1.6), 0, 1));
  const m = new THREE.MeshBasicNodeMaterial();
  m.transparent = true;
  m.depthWrite = false;
  m.side = THREE.DoubleSide;
  m.blending = THREE.AdditiveBlending;
  m.forceSinglePass = true;
  m.fog = false;
  m.colorNode = col;
  m.opacityNode = clamp(along.mul(across).add(core.mul(0.6)), 0, 1).mul(float(1));
  return m;
}

/** 활성 화살 한 발(형식 배열 대신 고정 객체 풀: cap개만 만든다) */
class Shot {
  x = 0; y = 0; z = 0;
  /** 단위 방향(3D) */
  dx = 0; dy = 0; dz = 1;
  speed = 30;
  range = 10;
  kind = 0;
  /** 날아간 거리(m) */
  dist = 0;
  /** 사거리 끝에서 머리가 멈춘 뒤 흐른 시간(초): 꼬리가 따라잡는다 */
  tailAge = 0;
  /** 이번 프레임 위치 */
  px = 0; py = 0; pz = 0;
  /** 게임 화살 슬롯(Ev.Arrow flags >> 8, -1 = 표시 전용). Ev.ArrowEnd가 이 값으로 찾아 끝낸다(c022 렌더 연결) */
  tag = -1;
}

/** 꼬리가 사거리 끝에서 따라잡아 사라지는 시간(초) */
const TAIL_CATCH = 0.08;

export class ArrowRenderer {
  readonly group = new THREE.Group();
  readonly capacity: number;
  readonly body: THREE.InstancedMesh;
  readonly trail: THREE.InstancedMesh;
  private readonly shots: Shot[] = [];
  private n = 0;
  private readonly mtx = new THREE.Matrix4();
  private readonly col = new THREE.Color();
  private gain = 1;
  /** QA: 가득 차서 덮어쓴 횟수, 지금까지 쏜 수, 사거리 끝까지 간 수 */
  replaced = 0;
  fired = 0;
  landed = 0;
  /**
   * 화살이 사거리 끝(또는 바닥)에 닿을 때 한 번 부른다(선택). 착탄 VFX를 잇는 쪽이 넘긴다. 할당 없이 숫자만 넘긴다.
   * 게임 판정과 별개인 표시용이다(실제 피해·폭발 큐는 게임 이벤트가 정본).
   */
  onLand: ((x: number, y: number, z: number, kind: number) => void) | null = null;

  constructor(cap = ARROW_CAP) {
    this.capacity = cap;
    this.group.name = 'fx-arrows';
    this.body = new THREE.InstancedMesh(buildArrowGeometry(), bodyMaterial(), cap);
    this.trail = new THREE.InstancedMesh(buildTrailGeometry(), trailMaterial(), cap);
    for (const m of [this.body, this.trail]) {
      m.count = 0;
      m.frustumCulled = false;
      m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    }
    this.body.name = 'fx-arrow-body';
    this.trail.name = 'fx-arrow-trail';
    this.body.renderOrder = 2;
    this.trail.renderOrder = 22;
    // 인스턴스 색 버퍼를 생성 때 만든다(setColorAt 첫 호출의 지연 할당을 피한다)
    this.col.setRGB(1, 1, 1);
    for (let i = 0; i < cap; i++) { this.body.setColorAt(i, this.col); this.trail.setColorAt(i, this.col); this.shots.push(new Shot()); }
    this.body.instanceColor!.setUsage(THREE.DynamicDrawUsage);
    this.trail.instanceColor!.setUsage(THREE.DynamicDrawUsage);
    this.body.visible = this.trail.visible = false;
    this.group.add(this.body, this.trail);
  }

  get activeCount(): number { return this.n; }
  get drawCalls(): number { return this.n > 0 ? 2 : 0; }

  /** 섬광 약화(게임 설정): 꼬리 밝기 70% */
  setReduceFlash(on: boolean): void { this.gain = on ? 0.7 : 1; }

  private take(): Shot {
    if (this.n < this.capacity) return this.shots[this.n++];
    // 가득 참: 가장 많이 진행한 화살을 덮어쓴다
    let best = 0, bf = -1;
    for (let i = 0; i < this.n; i++) {
      const s = this.shots[i];
      const f = s.dist / Math.max(0.01, s.range) + s.tailAge;
      if (f > bf) { bf = f; best = i; }
    }
    this.replaced++;
    return this.shots[best];
  }

  /**
   * 화살 한 발(Ev.Arrow 한 건). dirX·dirZ는 바닥 평면 방향(길이 0이면 +Z), speed m/s, range m, kind = ArrowKind, y = 발사 높이.
   * 공중(4)은 사거리 끝 바닥으로 내리꽂힌다. 반환: 쓴 슬롯이 덮어쓰기였으면 false
   */
  fire(x: number, y: number, z: number, dirX: number, dirZ: number, speed: number, range: number, kind: number, tag = -1, lead = 0): boolean {
    const full = this.n >= this.capacity;
    const s = this.take();
    s.tag = tag | 0;
    let l = Math.hypot(dirX, dirZ);
    if (l < 1e-6) { dirX = 0; dirZ = 1; l = 1; }
    const k = kind | 0;
    s.kind = k >= 0 && k < ARROW_KIND_COUNT ? k : 0;
    s.x = x; s.y = y; s.z = z;
    s.speed = Math.max(1, speed);
    s.range = Math.max(0.3, range);
    if (s.kind === ArrowKind.Air) {
      // 높이 y → 바닥 근처까지 수평 range 동안 내려간다(대각 길이를 사거리로 쓴다)
      const drop = Math.max(0, y - AIR_DROP_TO);
      const L = Math.hypot(s.range, drop);
      s.dx = (dirX / l) * (s.range / L); s.dz = (dirZ / l) * (s.range / L); s.dy = -drop / L;
      s.range = L;
    } else {
      s.dx = dirX / l; s.dy = 0; s.dz = dirZ / l;
    }
    // lead(초) = 이벤트가 늦게 도착한 만큼 이미 날아간 시간(렌더가 게임 판정 위치를 따라잡는다)
    s.dist = Math.min(s.range, Math.max(0, lead) * s.speed); s.tailAge = 0;
    s.px = x; s.py = y; s.pz = z;
    this.fired++;
    this.write();
    return !full;
  }

  /**
   * 게임 화살이 끝났다(Ev.ArrowEnd: 적중·벽·사거리). tag 슬롯의 날아가는 화살을 (x,z) 끝점에서 멈춰 꼬리만 따라잡게 한다.
   * 표시가 게임보다 앞서 있으면 끝점으로 되돌리고, 뒤처져 있으면 끝점까지 바로 옮긴다(판정과 어긋나 보이지 않게). 반환: 찾았으면 true
   */
  end(tag: number, x: number, z: number): boolean {
    if (tag < 0) return false;
    for (let i = 0; i < this.n; i++) {
      const s = this.shots[i];
      if (s.tag !== tag || s.dist >= s.range) continue;
      // 바닥 평면 투영 거리(공중 화살은 대각 비율로 늘린다)
      const h = Math.hypot(s.dx, s.dz);
      const along = h > 1e-6 ? ((x - s.x) * s.dx + (z - s.z) * s.dz) / (h * h) : s.dist;
      s.range = Math.max(0.05, Math.min(s.range, along));
      s.dist = s.range;
      s.tag = -1;
      this.landed++;
      this.write();
      return true;
    }
    return false;
  }

  /**
   * 하늘에서 떨어지는 화살(화살비 한 발): (x,z) 바닥으로 height m 위에서 낙하. 살짝 기울인다(tilt rad, yaw 방향).
   * lead(초) = 이미 떨어진 시간: 낙하 중간에서 시작해 착탄 시각을 판정 틱에 맞춘다
   */
  rain(x: number, z: number, height = 7, tilt = 0.18, yaw = 0, speed = RAIN_SPEED, lead = 0): boolean {
    const full = this.n >= this.capacity;
    const s = this.take();
    const st = Math.sin(tilt), ct = Math.cos(tilt);
    s.kind = ArrowKind.Rain;
    s.tag = -1;
    s.dx = Math.sin(yaw) * st; s.dz = Math.cos(yaw) * st; s.dy = -ct;
    s.range = height / ct;
    s.x = x - s.dx * s.range; s.y = height; s.z = z - s.dz * s.range;
    s.speed = Math.max(1, speed);
    s.dist = Math.min(s.range * 0.98, Math.max(0, lead) * s.speed); s.tailAge = 0;
    s.px = s.x; s.py = s.y; s.pz = s.z;
    this.fired++;
    this.write();
    return !full;
  }

  /** dt초(영웅 시계) 진행 */
  update(dt: number): void {
    if (this.n === 0) return;
    let i = 0;
    while (i < this.n) {
      const s = this.shots[i];
      if (s.dist < s.range) {
        s.dist += s.speed * dt;
        if (s.dist >= s.range) {
          s.dist = s.range;
          this.landed++;
          const cb = this.onLand;
          if (cb !== null) cb(s.x + s.dx * s.range, s.y + s.dy * s.range, s.z + s.dz * s.range, s.kind);
        }
      } else {
        s.tailAge += dt;
      }
      if (s.tailAge >= TAIL_CATCH) {
        const j = --this.n;
        if (i !== j) { this.shots[i] = this.shots[j]; this.shots[j] = s; }
        continue;
      }
      i++;
    }
    this.write();
  }

  /** 모든 화살을 지운다(장면 전환·스킬 끊김) */
  clear(): void {
    this.n = 0;
    this.write();
  }

  private write(): void {
    const n = this.n, e = this.mtx.elements;
    for (let i = 0; i < n; i++) {
      const s = this.shots[i];
      const look = ARROW_LOOK[s.kind];
      const hx = s.x + s.dx * s.dist, hy = s.y + s.dy * s.dist, hz = s.z + s.dz * s.dist;
      s.px = hx; s.py = hy; s.pz = hz;
      // 기저: 앞 f = d, 오른쪽 r = up × f(정규화), 위 u = f × r. f가 거의 수직이면 r = +X
      const fx = s.dx, fy = s.dy, fz = s.dz;
      let rx = fz, ry = 0, rz = -fx;
      let rl = Math.hypot(rx, rz);
      if (rl < 1e-4) { rx = 1; rz = 0; rl = 1; }
      rx /= rl; rz /= rl;
      const ux = fy * rz - fz * ry, uy = fz * rx - fx * rz, uz = fx * ry - fy * rx;
      // 몸통: 꼬리가 따라잡는 동안(사거리 끝) 줄어든다(박힌 화살이 남지 않게)
      const fade = 1 - Math.min(1, s.tailAge / TAIL_CATCH);
      const b = look.body * (0.25 + 0.75 * fade);
      e[0] = rx * b; e[1] = ry * b; e[2] = rz * b; e[3] = 0;
      e[4] = ux * b; e[5] = uy * b; e[6] = uz * b; e[7] = 0;
      e[8] = fx * b; e[9] = fy * b; e[10] = fz * b; e[11] = 0;
      e[12] = hx; e[13] = hy; e[14] = hz; e[15] = 1;
      this.body.setMatrixAt(i, this.mtx);
      this.col.setRGB(look.tint[0], look.tint[1], look.tint[2]);
      this.body.setColorAt(i, this.col);
      // 꼬리: 지나온 거리만큼만(출발점 뒤로 넘지 않게), 사거리 끝에서는 머리 쪽으로 줄어든다
      const want = Math.min(look.trailMax, look.trailSec * s.speed);
      const len = Math.max(0.02, Math.min(want, s.dist) * fade);
      const w = look.trailW;
      e[0] = rx * w; e[1] = ry * w; e[2] = rz * w;
      e[4] = ux * w; e[5] = uy * w; e[6] = uz * w;
      e[8] = fx * len; e[9] = fy * len; e[10] = fz * len;
      this.trail.setMatrixAt(i, this.mtx);
      const g = look.glow * this.gain * (0.35 + 0.65 * fade) * Math.min(1, s.dist / 0.3 + 0.3);
      this.col.setRGB(g, g, g);
      this.trail.setColorAt(i, this.col);
    }
    this.body.count = n;
    this.trail.count = n;
    this.body.visible = this.trail.visible = n > 0;
    if (n > 0) {
      this.body.instanceMatrix.needsUpdate = true;
      this.trail.instanceMatrix.needsUpdate = true;
      this.body.instanceColor!.needsUpdate = true;
      this.trail.instanceColor!.needsUpdate = true;
    }
  }

  /** i번째 활성 화살 머리 위치(QA·시험용, out에 x,y,z) */
  headOf(i: number, out: Float32Array | number[]): boolean {
    if (i < 0 || i >= this.n) return false;
    const s = this.shots[i];
    out[0] = s.px; out[1] = s.py; out[2] = s.pz;
    return true;
  }

  /** i번째 활성 화살 종류(QA) */
  kindOf(i: number): number { return i >= 0 && i < this.n ? this.shots[i].kind : -1; }

  /** 미리 컴파일용: 한 발을 보이게 둔다 */
  prewarm(on: boolean): void {
    if (on) { this.body.visible = this.trail.visible = true; this.body.count = this.trail.count = Math.max(1, this.n); }
    else this.write();
  }

  dispose(): void {
    this.body.geometry.dispose();
    this.trail.geometry.dispose();
    (this.body.material as THREE.Material).dispose();
    (this.trail.material as THREE.Material).dispose();
    this.body.dispose();
    this.trail.dispose();
    this.group.remove(this.body, this.trail);
    this.n = 0;
  }
}
